import { get, post, put } from '../api';
import { state } from '../state';
import { showToast } from '../components/toast';

/* ── types ───────────────────────────────────────────────────────── */
interface AiTask {
  id: string;
  server_id: string;
  title: string;
  description: string;
  status: string;
  assigned_to: string;
  created_by: string;
  priority: number;
  cc_notes: string;
  result: string;
  created_at: string;
  updated_at: string;
}

const STATUS_ORDER = ['in_progress', 'pending', 'needs_cc_review', 'blocked', 'done'] as const;
const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  needs_cc_review: 'Needs CC Review',
  blocked: 'Blocked',
  done: 'Done',
};
const STATUS_COLORS: Record<string, string> = {
  pending: 'var(--fg-muted)',
  in_progress: 'var(--accent, #16a34a)',
  needs_cc_review: 'var(--warning, #e9a033)',
  blocked: 'var(--danger, #e54d4d)',
  done: 'var(--fg-muted)',
};
const AGENT_LABELS: Record<string, string> = {
  local_ai: 'Local AI',
  cc: 'Claude Code',
  mitchell: 'Mitchell',
};

let pollTimer: ReturnType<typeof setInterval> | null = null;

/* ── cleanup ─────────────────────────────────────────────────────── */
export function cleanupAiTasks(): void {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
}

/* ── render ───────────────────────────────────────────────────────── */
export async function renderAiTasks(container: HTMLElement): Promise<void> {
  const serverId = state.currentServer ?? '';
  container.innerHTML = `<div class="ait-wrap">
    <div class="ait-header">
      <h2 class="ait-title">${svgClipboard()} AI Task Board</h2>
      <button class="ait-add-btn" id="ait-add" type="button">${svgPlus()} New Task</button>
    </div>
    <div class="ait-filters" id="ait-filters">
      <button class="ait-filter ait-filter-active" data-f="all">All</button>
      <button class="ait-filter" data-f="local_ai">Local AI</button>
      <button class="ait-filter" data-f="cc">CC</button>
      <button class="ait-filter" data-f="needs_cc_review">Needs Review</button>
    </div>
    <div class="ait-board" id="ait-board"></div>
  </div>`;

  injectStyles();

  let currentFilter = 'all';

  const board = container.querySelector('#ait-board') as HTMLElement;
  const filtersEl = container.querySelector('#ait-filters') as HTMLElement;
  const addBtn = container.querySelector('#ait-add') as HTMLElement;

  async function load(): Promise<void> {
    const params = new URLSearchParams({ server_id: serverId });
    if (currentFilter === 'needs_cc_review') params.set('status', 'needs_cc_review');
    else if (currentFilter !== 'all') params.set('assigned_to', currentFilter);

    const tasks = await get<AiTask[]>(`/api/ai-tasks?${params}`).catch(() => [] as AiTask[]);
    renderBoard(board, tasks);
  }

  function renderBoard(el: HTMLElement, tasks: AiTask[]): void {
    if (!tasks.length) {
      el.innerHTML = `<div class="ait-empty">No tasks yet. The local AI will post tasks here as it works.</div>`;
      return;
    }

    // Group by status
    const grouped: Record<string, AiTask[]> = {};
    for (const s of STATUS_ORDER) grouped[s] = [];
    for (const t of tasks) {
      const bucket = grouped[t.status] ?? (grouped['pending'] = grouped['pending'] || []);
      (grouped[t.status] || bucket).push(t);
    }

    let html = '';
    for (const s of STATUS_ORDER) {
      const items = grouped[s];
      if (!items || !items.length) continue;
      html += `<div class="ait-col">
        <div class="ait-col-header">
          <span class="ait-col-dot" style="background:${STATUS_COLORS[s] ?? 'var(--fg-muted)'}"></span>
          <span class="ait-col-label">${esc(STATUS_LABELS[s] ?? s)}</span>
          <span class="ait-col-count">${items.length}</span>
        </div>`;
      for (const t of items) {
        html += renderCard(t);
      }
      html += '</div>';
    }
    el.innerHTML = html;

    // Bind card actions
    el.querySelectorAll('.ait-card-status').forEach(btn => {
      btn.addEventListener('click', () => void handleStatusChange(btn as HTMLElement, load));
    });
    el.querySelectorAll('.ait-card').forEach(card => {
      card.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('.ait-card-status')) return;
        openTaskDetail(card.getAttribute('data-id') ?? '', load);
      });
    });
  }

  filtersEl.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('.ait-filter') as HTMLElement | null;
    if (!btn) return;
    filtersEl.querySelectorAll('.ait-filter').forEach(b => b.classList.remove('ait-filter-active'));
    btn.classList.add('ait-filter-active');
    currentFilter = btn.getAttribute('data-f') ?? 'all';
    void load();
  });

  addBtn.addEventListener('click', () => openCreateModal(serverId, load));

  await load();
  pollTimer = setInterval(() => void load(), 15000);
}

/* ── card html ───────────────────────────────────────────────────── */
function renderCard(t: AiTask): string {
  const age = timeAgo(t.updated_at || t.created_at);
  const prioClass = t.priority >= 2 ? 'ait-prio-high' : t.priority === 1 ? 'ait-prio-med' : '';
  const nextStatuses = getNextStatuses(t.status);

  return `<div class="ait-card ${prioClass}" data-id="${esc(t.id)}">
    <div class="ait-card-top">
      <span class="ait-card-title">${esc(t.title)}</span>
    </div>
    ${t.description ? `<div class="ait-card-desc">${esc(t.description)}</div>` : ''}
    ${t.result ? `<div class="ait-card-result">${svgCheck()} ${esc(t.result)}</div>` : ''}
    ${t.cc_notes ? `<div class="ait-card-notes">${svgNote()} ${esc(t.cc_notes)}</div>` : ''}
    <div class="ait-card-meta">
      <span class="ait-card-agent">${esc(AGENT_LABELS[t.assigned_to] ?? t.assigned_to)}</span>
      <span class="ait-card-time">${age}</span>
    </div>
    ${nextStatuses.length ? `<div class="ait-card-actions">
      ${nextStatuses.map(s => `<button class="ait-card-status" data-id="${esc(t.id)}" data-status="${s}" type="button">${esc(STATUS_LABELS[s] ?? s)}</button>`).join('')}
    </div>` : ''}
  </div>`;
}

function getNextStatuses(current: string): string[] {
  switch (current) {
    case 'pending': return ['in_progress'];
    case 'in_progress': return ['done', 'needs_cc_review', 'blocked'];
    case 'needs_cc_review': return ['in_progress', 'done'];
    case 'blocked': return ['in_progress'];
    case 'done': return [];
    default: return ['pending'];
  }
}

/* ── handlers ────────────────────────────────────────────────────── */
async function handleStatusChange(btn: HTMLElement, reload: () => Promise<void>): Promise<void> {
  const id = btn.getAttribute('data-id') ?? '';
  const status = btn.getAttribute('data-status') ?? '';
  if (!id || !status) return;
  await put<AiTask>('/api/ai-tasks/update', { id, status }).catch(() => null);
  await reload();
}

function openCreateModal(serverId: string, reload: () => Promise<void>): void {
  const overlay = document.createElement('div');
  overlay.className = 'ait-modal-overlay';
  overlay.innerHTML = `<div class="ait-modal">
    <h3>New AI Task</h3>
    <input class="ait-input" id="ait-new-title" placeholder="Task title" maxlength="200" />
    <textarea class="ait-textarea" id="ait-new-desc" placeholder="Description (optional)" maxlength="2000"></textarea>
    <div class="ait-modal-row">
      <label>Assign to</label>
      <select class="ait-select" id="ait-new-assign">
        <option value="local_ai">Local AI</option>
        <option value="cc">Claude Code</option>
      </select>
    </div>
    <div class="ait-modal-row">
      <label>Priority</label>
      <select class="ait-select" id="ait-new-prio">
        <option value="0">Normal</option>
        <option value="1">Medium</option>
        <option value="2">High</option>
      </select>
    </div>
    <div class="ait-modal-btns">
      <button class="ait-btn-cancel" id="ait-cancel" type="button">Cancel</button>
      <button class="ait-btn-create" id="ait-create" type="button">Create</button>
    </div>
  </div>`;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  overlay.querySelector('#ait-cancel')?.addEventListener('click', () => overlay.remove());
  overlay.querySelector('#ait-create')?.addEventListener('click', async () => {
    const title = (overlay.querySelector('#ait-new-title') as HTMLInputElement).value.trim();
    if (!title) { showToast('Title is required', 'warn'); return; }
    const desc = (overlay.querySelector('#ait-new-desc') as HTMLTextAreaElement).value.trim();
    const assign = (overlay.querySelector('#ait-new-assign') as HTMLSelectElement).value;
    const prio = parseInt((overlay.querySelector('#ait-new-prio') as HTMLSelectElement).value, 10);
    await post<AiTask>('/api/ai-tasks/create', {
      server_id: serverId, title, description: desc, assigned_to: assign, priority: prio,
    }).catch((e: Error) => { showToast(e.message, 'err'); return null; });
    overlay.remove();
    await reload();
  });
  (overlay.querySelector('#ait-new-title') as HTMLInputElement)?.focus();
}

function openTaskDetail(id: string, reload: () => Promise<void>): void {
  const overlay = document.createElement('div');
  overlay.className = 'ait-modal-overlay';
  overlay.innerHTML = `<div class="ait-modal ait-modal-wide"><div class="ait-loading">Loading...</div></div>`;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });

  void (async () => {
    const serverId = state.currentServer ?? '';
    const tasks = await get<AiTask[]>(`/api/ai-tasks?server_id=${encodeURIComponent(serverId)}`).catch(() => []);
    const task = tasks.find(t => t.id === id);
    if (!task) { overlay.remove(); return; }

    const modal = overlay.querySelector('.ait-modal') as HTMLElement;
    modal.innerHTML = `
      <h3>${esc(task.title)}</h3>
      <div class="ait-detail-grid">
        <div class="ait-detail-label">Status</div><div><span class="ait-badge" style="background:${STATUS_COLORS[task.status] ?? 'var(--fg-muted)'}">${esc(STATUS_LABELS[task.status] ?? task.status)}</span></div>
        <div class="ait-detail-label">Assigned</div><div>${esc(AGENT_LABELS[task.assigned_to] ?? task.assigned_to)}</div>
        <div class="ait-detail-label">Priority</div><div>${task.priority >= 2 ? 'High' : task.priority === 1 ? 'Medium' : 'Normal'}</div>
        <div class="ait-detail-label">Created</div><div>${esc(task.created_at)}</div>
        <div class="ait-detail-label">Updated</div><div>${esc(task.updated_at)}</div>
      </div>
      ${task.description ? `<div class="ait-detail-section"><strong>Description</strong><p>${esc(task.description)}</p></div>` : ''}
      ${task.result ? `<div class="ait-detail-section"><strong>Result</strong><p>${esc(task.result)}</p></div>` : ''}
      <div class="ait-detail-section">
        <strong>CC Notes</strong>
        <textarea class="ait-textarea" id="ait-detail-notes" placeholder="Add notes for CC to read...">${esc(task.cc_notes)}</textarea>
      </div>
      <div class="ait-modal-btns">
        <button class="ait-btn-cancel" id="ait-detail-close" type="button">Close</button>
        <button class="ait-btn-create" id="ait-detail-save" type="button">Save Notes</button>
      </div>`;

    modal.querySelector('#ait-detail-close')?.addEventListener('click', () => overlay.remove());
    modal.querySelector('#ait-detail-save')?.addEventListener('click', async () => {
      const notes = (modal.querySelector('#ait-detail-notes') as HTMLTextAreaElement).value;
      await put<AiTask>('/api/ai-tasks/update', { id: task.id, cc_notes: notes }).catch(() => null);
      showToast('Notes saved', 'ok');
      overlay.remove();
      await reload();
    });
  })();
}

/* ── helpers ──────────────────────────────────────────────────────── */
function timeAgo(iso: string): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso + 'Z').getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/* ── icons (SVG, no emoji) ───────────────────────────────────────── */
function svgClipboard(): string {
  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle;margin-right:6px"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>';
}
function svgPlus(): string {
  return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
}
function svgCheck(): string {
  return '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent, #16a34a)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle;margin-right:4px"><polyline points="20 6 9 17 4 12"/></svg>';
}
function svgNote(): string {
  return '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--warning, #e9a033)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle;margin-right:4px"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>';
}

/* ── styles ───────────────────────────────────────────────────────── */
let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;
  const s = document.createElement('style');
  s.textContent = `
.ait-wrap { padding: 20px; max-width: 900px; margin: 0 auto; height: 100%; overflow-y: auto; }
.ait-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.ait-title { font-size: 18px; font-weight: 700; color: var(--fg); display: flex; align-items: center; }
.ait-add-btn { display: flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: 6px; border: 1px solid var(--border); background: var(--bg-secondary); color: var(--fg); font-size: 13px; font-weight: 600; cursor: pointer; }
.ait-add-btn:hover { background: var(--bg-tertiary); }
.ait-filters { display: flex; gap: 6px; margin-bottom: 16px; flex-wrap: wrap; }
.ait-filter { padding: 4px 12px; border-radius: 20px; border: 1px solid var(--border); background: transparent; color: var(--fg-muted); font-size: 12px; cursor: pointer; }
.ait-filter:hover { background: var(--bg-secondary); }
.ait-filter-active { background: var(--accent, #16a34a); color: #fff; border-color: var(--accent, #16a34a); }
.ait-board { display: flex; flex-direction: column; gap: 20px; }
.ait-col { }
.ait-col-header { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid var(--border); }
.ait-col-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.ait-col-label { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--fg-muted); }
.ait-col-count { font-size: 11px; color: var(--fg-muted); background: var(--bg-secondary); padding: 0 6px; border-radius: 10px; }
.ait-card { padding: 12px; border-radius: 8px; border: 1px solid var(--border); background: var(--bg-secondary); cursor: pointer; margin-bottom: 6px; transition: border-color 0.15s; }
.ait-card:hover { border-color: var(--accent, #16a34a); }
.ait-prio-high { border-left: 3px solid var(--danger, #e54d4d); }
.ait-prio-med { border-left: 3px solid var(--warning, #e9a033); }
.ait-card-top { margin-bottom: 4px; }
.ait-card-title { font-size: 14px; font-weight: 600; color: var(--fg); }
.ait-card-desc { font-size: 12px; color: var(--fg-muted); margin-bottom: 6px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.ait-card-result { font-size: 12px; color: var(--accent, #16a34a); margin-bottom: 4px; }
.ait-card-notes { font-size: 12px; color: var(--warning, #e9a033); margin-bottom: 4px; }
.ait-card-meta { display: flex; justify-content: space-between; font-size: 11px; color: var(--fg-muted); margin-top: 6px; }
.ait-card-agent { background: var(--bg-tertiary); padding: 1px 8px; border-radius: 10px; }
.ait-card-actions { display: flex; gap: 4px; margin-top: 8px; flex-wrap: wrap; }
.ait-card-status { font-size: 11px; padding: 2px 8px; border-radius: 4px; border: 1px solid var(--border); background: transparent; color: var(--fg-muted); cursor: pointer; }
.ait-card-status:hover { background: var(--bg-tertiary); color: var(--fg); }
.ait-empty { text-align: center; padding: 40px 20px; color: var(--fg-muted); font-size: 14px; }
.ait-loading { text-align: center; padding: 20px; color: var(--fg-muted); }

/* modals */
.ait-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; }
.ait-modal { background: var(--bg); border-radius: 12px; padding: 24px; width: 90%; max-width: 440px; max-height: 90vh; overflow-y: auto; }
.ait-modal-wide { max-width: 560px; }
.ait-modal h3 { font-size: 16px; font-weight: 700; margin-bottom: 14px; color: var(--fg); }
.ait-input { width: 100%; padding: 8px 12px; border: 1px solid var(--border); border-radius: 6px; background: var(--bg-secondary); color: var(--fg); font-size: 14px; margin-bottom: 10px; }
.ait-textarea { width: 100%; padding: 8px 12px; border: 1px solid var(--border); border-radius: 6px; background: var(--bg-secondary); color: var(--fg); font-size: 13px; min-height: 70px; resize: vertical; margin-bottom: 10px; font-family: inherit; }
.ait-select { padding: 6px 10px; border: 1px solid var(--border); border-radius: 6px; background: var(--bg-secondary); color: var(--fg); font-size: 13px; }
.ait-modal-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.ait-modal-row label { font-size: 13px; color: var(--fg-muted); }
.ait-modal-btns { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
.ait-btn-cancel { padding: 6px 16px; border-radius: 6px; border: 1px solid var(--border); background: transparent; color: var(--fg-muted); font-size: 13px; cursor: pointer; }
.ait-btn-create { padding: 6px 16px; border-radius: 6px; border: none; background: var(--accent, #16a34a); color: #fff; font-size: 13px; font-weight: 600; cursor: pointer; }
.ait-btn-create:hover { opacity: 0.9; }
.ait-badge { display: inline-block; padding: 2px 10px; border-radius: 10px; color: var(--fg-on-accent, #fff); font-size: 12px; font-weight: 600; }
.ait-detail-grid { display: grid; grid-template-columns: 90px 1fr; gap: 6px 12px; margin-bottom: 16px; font-size: 13px; }
.ait-detail-label { color: var(--fg-muted); font-weight: 600; }
.ait-detail-section { margin-bottom: 12px; }
.ait-detail-section strong { font-size: 13px; display: block; margin-bottom: 4px; color: var(--fg); }
.ait-detail-section p { font-size: 13px; color: var(--fg-muted); white-space: pre-wrap; }
`;
  document.head.appendChild(s);
}
